import { Button } from '@akin-home/buttons/native';
import { Text } from '@akin-home/text/native';
import * as React from 'react';
import { TextStyle, View, ViewStyle } from 'react-native';
import styled from 'styled-components/native';
import { dimensions, palette, spacing } from '../../theme';
import Shadow from '../Shadow';
import { GetItemText, RenderListItem } from './TextListInputTypes';

export interface TextListInputProps {
  /**
   * items
   * An arbitrary array of objects to be rendered in the list
   */
  items?: any[];

  /**
   * onChange
   * Callback called when to value property changes either by the addition of a new list item or the removal of one
   */
  onPressItem(value: any, index: number): void;

  /**
   * renderListItm
   * optional callback, useful when you want custom representation of your data in the list e.g. if you are rendering
   * a list of people with avatars, this transform allows you to add the avatars and display the information in any
   * way you want
   */
  renderListItem?: RenderListItem;

  /**
   * getItemText
   * optional callback, useful when value is a list of objects. with this callback you can transform objects to the text
   * representation which ever way you want. If this callback is not passed, the component will assume that the list is a simple
   * list of strings
   */
  getItemText?: GetItemText;

  /**
   * optional style override
   */
  style?: ViewStyle;

  /**
   * optional style override
   */
  itemStyle?: ViewStyle;
}

const TEXT: TextStyle = {
  flex: 1,
};

// Default transforms, assume the data being passed is a list of strings
const renderListItemDefault = (
  item: any,
  getItemText: (item: any) => string
) => <Text style={TEXT}>{getItemText(item)}</Text>;

// Default transforms, assume the data being passed is a list of strings
const getItemTextDefault = (text: string): any => text;

/**
 * TextList renders an arbitrary lists of objects
 */
export const TextList = (props: TextListInputProps) => {
  const {
    onPressItem,
    style: styleOverride,
    itemStyle: itemStyleOverride,
    items = [],
    renderListItem = renderListItemDefault,
    getItemText = getItemTextDefault,
  } = props;

  if (!items) return null;

  return (
    <Container style={styleOverride}>
      {items?.map((item, index) => (
        <Item
          key={`${JSON.stringify(item)}-${index}`}
          style={itemStyleOverride}
        >
          {renderListItem(item, getItemText, index)}

          <View style={{ flexGrow: 0 }}>
            <Button
              icon="times"
              variant="secondary"
              onPress={() => onPressItem && onPressItem(item, index)}
            />
          </View>
        </Item>
      ))}
    </Container>
  );
};

const Container = styled.View`
  align-self: stretch;
`;

const Item = styled(Shadow)`
  background-color: ${palette.white};
  padding-left: ${spacing[4]}px;
  padding-right: ${spacing[2]}px;

  flex-direction: row;
  align-items: center;
  margin-top: ${spacing[4]}px;
  border-radius: ${dimensions.buttonRadius}px;
  height: 56px;
`;
